import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/assessment",  label: "Assessment" },
  { to: "/curriculum",  label: "Curriculum" },
  { to: "/activities",  label: "Activities" },
  { to: "/marketplace", label: "Shop", accent: true },
  { to: "/communities", label: "Communities" },
  { to: "/assistant",   label: "AI Assistant" },
  { to: "/about",       label: "About" },
];

const MORE = [
  { to: "/careers",           label: "Future Careers" },
  { to: "/tutors",            label: "Tutors" },
  { to: "/mentors",           label: "Mentors" },
  { to: "/impact",            label: "Learning for All" },
  { to: "/parent-readiness",  label: "Parent Readiness" },
  { to: "/pricing",           label: "Pricing" },
  { to: "/contact",           label: "Contact" },
  { to: "/survey",            label: "Website Survey" },
  { to: "/feedback",          label: "Share feedback" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const moreRef = useRef(null);
  const close = () => { setOpen(false); setMoreOpen(false); };

  // Click / touch outside handler for the "More" dropdown (reliable on tablet + laptop)
  useEffect(() => {
    if (!moreOpen) return;
    const handleOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [moreOpen]);

  return (
    <header className="sticky top-0 z-40 bg-brand-bg/85 backdrop-blur-xl border-b border-brand-blue/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 font-heading font-bold text-brand-blue text-xl shrink-0">
          <span className="inline-flex w-8 h-8 rounded-xl bg-brand-blue text-white items-center justify-center">
            <Sparkles size={16} strokeWidth={2} />
          </span>
          FuturePath
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors relative ${
                  n.accent ? "text-brand-orange hover:bg-brand-orange-soft" : ""
                } ${
                  isActive
                    ? "text-brand-blue bg-brand-divider"
                    : n.accent ? "" : "text-brand-muted hover:text-brand-blue"
                }`
              }
            >
              {n.label}
              {n.accent && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" />}
            </NavLink>
          ))}
          <div className="relative" ref={moreRef}>
            <button
              data-testid="nav-more"
              type="button"
              aria-haspopup="true"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((o) => !o)}
              className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                moreOpen ? "text-brand-blue bg-brand-divider" : "text-brand-muted hover:text-brand-blue"
              }`}
            >
              More ▾
            </button>
            {moreOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-2 w-56 py-2 bg-white rounded-2xl shadow-[0_20px_40px_-15px_rgba(15,41,66,0.25)] border border-brand-border z-[60]"
              >
                {MORE.map((m) => (
                  <Link
                    key={m.to}
                    to={m.to}
                    onClick={close}
                    data-testid={`more-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block px-4 py-2 text-sm text-brand-blue hover:bg-brand-bg"
                  >
                    {m.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="header-dashboard-link" className="btn-ghost">
                {user.name.split(" ")[0]}
              </Link>
              <button data-testid="header-logout-btn" onClick={() => { logout(); navigate("/"); }} className="btn-ghost" aria-label="Logout">
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="btn-ghost">Log in</Link>
              <Link to="/assessment" data-testid="header-cta-btn" className="btn-primary">Start</Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-brand-divider"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-border bg-brand-bg">
          <div className="px-4 py-3 flex flex-col gap-1">
            {[...NAV, ...MORE].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={close}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-2.5 rounded-xl text-sm font-medium text-brand-blue hover:bg-white"
              >
                {n.label}
              </NavLink>
            ))}
            <div className="border-t border-brand-border my-2" />
            {user ? (
              <>
                <Link to="/dashboard" onClick={close} className="btn-outline">Dashboard</Link>
                <button onClick={() => { logout(); close(); navigate("/"); }} className="btn-ghost">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={close} className="btn-outline">Log in</Link>
                <Link to="/assessment" onClick={close} className="btn-primary">Start assessment</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
